import {
  type CategoryResponse,
  type CategorySortField,
  getCategories,
  type SortDirection,
} from '@api/categories-api';
import type { PaginationMeta } from '@api/pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export interface CategoryFilters {
  search: string;
  sortBy?: CategorySortField;
  sortDir?: SortDirection;
  isClinic?: boolean;
  isGlobal?: boolean;
  countryId?: string;
}

interface UsePaginatedCategoriesResult {
  categories: CategoryResponse[];
  meta: PaginationMeta | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePaginatedCategories(
  page: number,
  pageSize: number,
  filters: CategoryFilters,
): UsePaginatedCategoriesResult {
  const queryClient = useQueryClient();
  const queryKey = [
    'categories',
    'paginated',
    page,
    pageSize,
    filters.search,
    filters.sortBy,
    filters.sortDir,
    filters.isClinic,
    filters.isGlobal,
    filters.countryId,
  ];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      getCategories({
        page,
        pageSize,
        search: filters.search || undefined,
        sortBy: filters.sortBy,
        sortDir: filters.sortDir,
        isClinic: filters.isClinic,
        isGlobal: filters.isGlobal,
        countryId: filters.countryId,
      }),
  });

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['categories', 'paginated'] });
  }, [queryClient]);

  return {
    categories: data?.data ?? [],
    meta: data?.meta ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load categories') : null,
    refetch,
  };
}
